import type { TFunction } from 'i18next';

import { ROUTE } from '../../../../routes';
import type { AdSubjectType } from '../../banner-kit-api';
import { LINK_FOR_PARAMS, type TrackingLink, type TrackingLinkKind } from '../../tracking-links-api';

/**
 * A landing page made FOR an asset's tracking link ("New landing page…" in the asset's link panel). The asset's
 * composer opens `/landing-pages/new` with the `LINK_FOR_PARAMS` query keys; the moment the new page is saved, that
 * asset version's link is pointed at it (spark-creatives keeps a landing page destination with no address until the
 * page is published, then builds the link from its address). From then on the LINK is the durable record: every tab
 * and device reads it back from the links landing on the page.
 *
 * The context itself is only an optimistic cache: saving the new page swaps the address to `/landing-pages/:id` in
 * place (without the query), so it is kept in sessionStorage under the brand and the page id for the rest of the
 * browser session. It carries what the server does not keep (the exact `return_to`, query included) and covers the
 * moment before the link is pointed at the page (or a pointing that failed, retried on the next open or publish).
 *
 * Pure helpers (no React), plus guarded sessionStorage reads and writes (a blocked or full store is never an error:
 * the context is then simply not kept).
 */

/** The asset kinds a landing page can be made for (a landing page's own link never routes through another page). */
export type LinkForKind = Exclude<TrackingLinkKind, 'landing_page'>;

const LINK_FOR_KINDS: readonly LinkForKind[] = ['video', 'kit', 'playable', 'banner'];

export interface LinkForContext {
  /** The asset (video ad, banner kit, mini-game, HTML5 banner). */
  creativeId: string;
  /** The asset version whose link moves to the page. */
  versionKey: string;
  kind: LinkForKind;
  /** The link's display name (the asset's title and version), also shown in the banner. */
  name: string;
  /** The asset's subject the page should feature, when given (a promotion banner id or a game id). */
  subjectType?: AdSubjectType;
  subjectId?: string;
  /** Where "Back to …" returns: a same-origin path under `/account/` (validated), else the asset's own route. */
  returnTo?: string;
  /** The asset's link was pointed at the page (spark-creatives answered); until then the composer retries it. */
  linked?: boolean;
}

const isLinkForKind = (value: string): value is LinkForKind => (LINK_FOR_KINDS as readonly string[]).includes(value);

const isSubjectType = (value: string): value is AdSubjectType =>
  value === 'promotion' || value === 'game' || value === 'gamification';

/** A `return_to` the app may navigate to: a path under `/account/` on this origin (its query and hash kept), else
 *  undefined. A scheme, a protocol-relative `//host`, a backslash or any other origin is refused. Pure. */
export const safeReturnTo = (raw: string | null | undefined): string | undefined => {
  const value = (raw ?? '').trim();
  if (!value.startsWith('/account/') || value.includes('\\')) return undefined;
  // The prefix check already rules out `//host` and schemes; the parse keeps only path, query and hash.
  try {
    const base = 'https://studio.invalid';
    const url = new URL(value, base);
    if (url.origin !== base || !url.pathname.startsWith('/account/')) return undefined;
    return `${url.pathname}${url.search}${url.hash}`;
  } catch {
    return undefined;
  }
};

/** The context a composer URL carries (`?for_creative=…&for_version=…`), or null when it carries none or an
 *  incomplete one (the asset, its version, its kind and the link's name are all required). Pure. */
export const linkForFromSearch = (search: string): LinkForContext | null => {
  const q = new URLSearchParams(search);
  const get = (key: string): string => (q.get(key) ?? '').trim();
  const creativeId = get(LINK_FOR_PARAMS.creativeId);
  const versionKey = get(LINK_FOR_PARAMS.versionKey);
  const kind = get(LINK_FOR_PARAMS.kind);
  const name = get(LINK_FOR_PARAMS.name);
  if (!creativeId || !versionKey || !name || !isLinkForKind(kind)) return null;
  const subjectType = get(LINK_FOR_PARAMS.subjectType);
  const subjectId = get(LINK_FOR_PARAMS.subjectId);
  const returnTo = safeReturnTo(q.get(LINK_FOR_PARAMS.returnTo));
  return {
    creativeId,
    versionKey,
    kind,
    name,
    ...(isSubjectType(subjectType) && subjectId ? { subjectType, subjectId } : {}),
    ...(returnTo ? { returnTo } : {}),
  };
};

/** A stored context read back, checked field by field (anything else stored there is ignored). Pure. */
export const parseLinkFor = (raw: unknown): LinkForContext | null => {
  if (typeof raw !== 'object' || raw === null) return null;
  const r = raw as Record<string, unknown>;
  const s = (v: unknown): string => (typeof v === 'string' ? v.trim() : '');
  const kind = s(r.kind);
  const creativeId = s(r.creativeId);
  const versionKey = s(r.versionKey);
  const name = s(r.name);
  if (!creativeId || !versionKey || !name || !isLinkForKind(kind)) return null;
  const subjectType = s(r.subjectType);
  const subjectId = s(r.subjectId);
  const returnTo = safeReturnTo(s(r.returnTo));
  return {
    creativeId,
    versionKey,
    kind,
    name,
    ...(isSubjectType(subjectType) && subjectId ? { subjectType, subjectId } : {}),
    ...(returnTo ? { returnTo } : {}),
    ...(r.linked === true ? { linked: true } : {}),
  };
};

/** Whether a link is the made-for asset's own link (same asset, same version). Pure. */
export const isLinkOf = (link: TrackingLink, ctx: LinkForContext): boolean =>
  link.creative_id === ctx.creativeId && link.version_key === ctx.versionKey;

const storageKey = (tenantId: number, pageId: string): string => `ows:lp-link-for:${tenantId}:${pageId}`;

/** Keep the context for the page (its id exists now) for the rest of the session. */
export const saveLinkFor = (tenantId: number, pageId: string, ctx: LinkForContext): void => {
  try {
    sessionStorage.setItem(storageKey(tenantId, pageId), JSON.stringify(ctx));
  } catch {
    // A blocked or full store: the context lives only as long as this composer.
  }
};

/** The context kept for the page, or null. */
export const loadLinkFor = (tenantId: number, pageId: string): LinkForContext | null => {
  try {
    const raw = sessionStorage.getItem(storageKey(tenantId, pageId));
    return raw ? parseLinkFor(JSON.parse(raw)) : null;
  } catch {
    return null;
  }
};

/** Drop the page's context (its asset's link points at it now). */
export const clearLinkFor = (tenantId: number, pageId: string): void => {
  try {
    sessionStorage.removeItem(storageKey(tenantId, pageId));
  } catch {
    // Nothing kept, nothing to drop.
  }
};

/** The asset kind's name (literal `t()` calls, so the extractor harvests them). */
export const trackingKindLabel = (kind: TrackingLinkKind, t: TFunction): string => {
  switch (kind) {
    case 'video':
      return t('Video ad');
    case 'kit':
      return t('Banner kit');
    case 'playable':
      return t('Mini-game');
    case 'banner':
      return t('HTML5 banner');
    default:
      return t('Landing page');
  }
};

/** The asset's composer route, by its link's kind. Pure. */
export const assetPath = (kind: TrackingLinkKind, creativeId: string): string => {
  switch (kind) {
    case 'video':
      return ROUTE.creativesVideo(creativeId);
    case 'kit':
      return ROUTE.creativesBannerKit(creativeId);
    case 'playable':
      return ROUTE.creativesPlayable(creativeId);
    case 'banner':
      return ROUTE.creativesHtml5Banner(creativeId);
    default:
      return ROUTE.creativesLandingPage(creativeId);
  }
};

/** Where an asset's name opens (a deep link to its composer): the saved `return_to` when it is that same asset's
 *  composer (its query kept, e.g. the video editor's `?mode=advanced`), else the asset's route. No asset composer
 *  opens a given version from its URL, so this is the asset itself. Pure. */
export const assetLinkPath = (kind: TrackingLinkKind, creativeId: string, ctx?: LinkForContext | null): string => {
  const route = assetPath(kind, creativeId);
  const back = ctx && ctx.kind === kind && ctx.creativeId === creativeId ? safeReturnTo(ctx.returnTo) : undefined;
  if (!back) return route;
  // A video ad opens under either video tab (Video Ads, Video Ads v2.0).
  const routes = kind === 'video' ? [route, ROUTE.creativesVideoV2(creativeId)] : [route];
  try {
    return routes.includes(new URL(back, 'https://studio.invalid').pathname) ? back : route;
  } catch {
    return route;
  }
};

/** The parts of a click on a deep link that decide who opens it. */
export interface DeepLinkClick {
  defaultPrevented: boolean;
  button: number;
  metaKey: boolean;
  ctrlKey: boolean;
  shiftKey: boolean;
  altKey: boolean;
  preventDefault: () => void;
}

/** A plain click on a deep link opens its path in the app (no reload); a modified one (a new tab or window) is left to
 *  the browser, which opens the link's own address. */
export const openInApp = (e: DeepLinkClick, path: string, onOpen: (path: string) => void): void => {
  if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
  e.preventDefault();
  onOpen(path);
};

/** Whether a link is the landing page's OWN link (not an asset's link routed through it). Pure. */
export const isOwnLink = (link: TrackingLink, pageId: string): boolean =>
  link.kind === 'landing_page' && link.creative_id === pageId;

/** How many asset links route through each landing page (by its spark id); a page's own link is not counted. Pure. */
export const linksPerLandingPage = (links: readonly TrackingLink[]): Map<string, number> => {
  const out = new Map<string, number>();
  for (const link of links) {
    const id = link.destination_type === 'landing_page' ? link.landing_page_id : '';
    if (!id || isOwnLink(link, id)) continue;
    out.set(id, (out.get(id) ?? 0) + 1);
  }
  return out;
};

/** Every link landing on each page (its own link included): what unpublishing it would cut off. Pure. */
export const allLinksPerLandingPage = (links: readonly TrackingLink[]): Map<string, number> => {
  const out = new Map<string, number>();
  for (const link of links) {
    const id = link.destination_type === 'landing_page' ? link.landing_page_id : '';
    if (id) out.set(id, (out.get(id) ?? 0) + 1);
  }
  return out;
};
